import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { type RefObject, useEffect, useLayoutEffect } from 'react';
import { easing } from './easing';
import { byMotionId, setupReveals } from './reveals';

gsap.registerPlugin(ScrollTrigger, SplitText);
const useBrowserLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/** Owns page decoration only; React state owns menus, tabs and form controls. */
export function useSiteMotion(rootRef: RefObject<HTMLElement | null>) {
  useBrowserLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    let disposed = false;
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    let context: gsap.Context | undefined;
    const setup = () => {
      const position = { left: window.scrollX, top: window.scrollY };
      context?.revert();
      context = gsap.context(() => {
        const reduced = preference.matches;
        const stopWords = reduced ? () => {} : setupWords(root);
        const stopHover = setupHover(root, reduced);
        const stopAmbient = setupAmbient(root, reduced);
        // Standalone triggers refresh immediately. Create them before the
        // one-shot reveal timelines, whose initialization waits for a tick.
        // Otherwise an anchored load can remove completed reveals during
        // ScrollTrigger's recursive scan of those pending timelines.
        const stopNavigation = setupNavigation(root, reduced);
        setupReveals(root, reduced);
        const wordTargets = root.querySelectorAll('[data-word-reveal="true"]');
        if (reduced && wordTargets.length) gsap.set(wordTargets, { visibility: 'visible' });
        return () => {
          stopHover();
          stopAmbient();
          stopNavigation();
          stopWords();
        };
      }, root);
      // Replacing all ScrollTriggers clears GSAP's saved scroll position.
      // Preserve the reader's place when the system motion preference changes.
      window.scrollTo({ ...position, behavior: 'instant' });
      ScrollTrigger.refresh();
    };
    setup();
    preference.addEventListener('change', setup);
    void document.fonts.ready.then(() => {
      if (!disposed) ScrollTrigger.refresh();
    });
    return () => {
      disposed = true;
      preference.removeEventListener('change', setup);
      context?.revert();
    };
  }, [rootRef]);
}

function setupWords(root: HTMLElement) {
  const contexts: gsap.Context[] = [];
  const initialized = new WeakSet<HTMLElement>();
  let disposed = false;
  let refreshFrame = 0;
  const texts = [...root.querySelectorAll<HTMLElement>('[data-word-reveal="true"]')].filter(
    (text) => text.textContent?.trim() && !text.querySelector('[data-word-reveal="true"]'),
  );
  if (!texts.length) return () => {};
  // Keep offscreen text in the authored pre-reveal state without constructing
  // thousands of character nodes for hidden panels or distant sections.
  // Opacity preserves the original offscreen text in the accessibility tree.
  gsap.set(texts, { opacity: 0 });
  function activate(text: HTMLElement) {
    if (disposed || initialized.has(text) || text.closest('[aria-hidden="true"], [inert]'))
      return false;
    initialized.add(text);
    const context = gsap.context(() => {
      const accessibleText = text.innerText;
      const split = SplitText.create(text, {
        aria: 'none',
        type: 'words, chars',
        mask: 'words',
        wordsClass: 'word',
        charsClass: 'char',
      });
      // A single original text reading, including on paragraphs where an
      // aria-label is invalid. Reverting the split removes this companion.
      split.words.forEach((word) => {
        word.setAttribute('aria-hidden', 'true');
      });
      const readable = document.createElement('span');
      readable.className = 'sr-only';
      readable.textContent = accessibleText;
      text.append(readable);
      const timeline = gsap
        .timeline({
          scrollTrigger: {
            trigger: text,
            start: 'top bottom',
            end: 'top 80%',
            toggleActions: 'none play none reset',
          },
        })
        .from(split.words, {
          yPercent: 110,
          delay: 0.2,
          duration: 0.8,
          stagger: { amount: 0.5 },
          ease: 'power1.out',
        });
      gsap.set(text, { opacity: 1, visibility: 'visible' });
      // Anchor jumps can enter beyond both trigger boundaries before this
      // near-viewport node is initialized. Play the same reveal in that case.
      if (text.getBoundingClientRect().top <= window.innerHeight * 0.8) timeline.play(0);
    }, text);
    contexts.push(context);
    return true;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      let changed = false;
      entries.forEach((entry) => {
        if (
          entry.isIntersecting &&
          entry.intersectionRect.width > 0 &&
          entry.intersectionRect.height > 0
        ) {
          const text = entry.target as HTMLElement;
          if (activate(text)) {
            observer.unobserve(text);
            changed = true;
          }
        }
      });
      if (changed) {
        cancelAnimationFrame(refreshFrame);
        refreshFrame = requestAnimationFrame(() => {
          if (!disposed) ScrollTrigger.refresh();
        });
      }
    },
    { rootMargin: '200px 0px' },
  );
  texts.forEach((text) => {
    const rect = text.getBoundingClientRect();
    const nearViewport =
      rect.width > 0 &&
      rect.height > 0 &&
      rect.top < window.innerHeight + 200 &&
      rect.bottom > -200;
    if (!nearViewport || !activate(text)) observer.observe(text);
  });
  return () => {
    disposed = true;
    cancelAnimationFrame(refreshFrame);
    observer.disconnect();
    contexts.forEach((context) => {
      context.revert();
    });
  };
}

function setupHover(root: HTMLElement, reduced: boolean) {
  const removeListeners: (() => void)[] = [];
  const families = [
    { parent: '.hero_element-wrapper', child: '.hero_element-hover', axis: 'height', rest: 0 },
    { parent: '.footer_element-wrapper', child: '.footer_element-hover', axis: 'height', rest: 0 },
    { parent: '.button_link', child: '.button_link-hover', axis: 'height', rest: 4 },
    { parent: '.tabs_link', child: '.div-block-4.hover', axis: 'width', rest: 0 },
    {
      parent: '.nav_menu-element-wrapper',
      child: '.nav_menu-element_hover',
      axis: 'height',
      rest: 0,
    },
  ] as const;
  families.forEach(({ parent, child, axis, rest }) => {
    root.querySelectorAll<HTMLElement>(parent).forEach((element) => {
      const targets = element.querySelectorAll<HTMLElement>(child);
      if (!targets.length) return;
      let animation: gsap.core.Tween | undefined;
      const change = (active: boolean) => {
        animation?.kill();
        animation = gsap.to(targets, {
          [axis]: `${active ? 100 : rest}%`,
          duration: reduced ? 0 : 0.3,
          ease: easing.cubicOut,
          overwrite: 'auto',
        });
      };
      const enter = () => change(true);
      const leave = () => change(false);
      element.addEventListener('pointerenter', enter);
      element.addEventListener('pointerleave', leave);
      element.addEventListener('focusin', enter);
      element.addEventListener('focusout', leave);
      removeListeners.push(() => {
        animation?.kill();
        element.removeEventListener('pointerenter', enter);
        element.removeEventListener('pointerleave', leave);
        element.removeEventListener('focusin', enter);
        element.removeEventListener('focusout', leave);
      });
    });
  });
  return () => {
    removeListeners.forEach((remove) => {
      remove();
    });
  };
}

function setupAmbient(root: HTMLElement, reduced: boolean) {
  const loops: { timeline: gsap.core.Timeline; trigger: ScrollTrigger }[] = [];
  function observe(element: HTMLElement, timeline: gsap.core.Timeline) {
    const trigger = ScrollTrigger.create({
      trigger: element,
      start: 'top bottom',
      end: 'bottom top',
      onEnter: () => timeline.play(),
      onEnterBack: () => timeline.play(),
      onLeave: () => timeline.pause(),
      onLeaveBack: () => timeline.pause(),
    });
    loops.push({ timeline, trigger });
  }
  ['afc16972-184a-f674-57da-da840944b13c', '0f5b3e13-42d6-4d35-edf4-cfb9f6717e45'].forEach((id) => {
    const element = byMotionId(root, id);
    const grid = element?.querySelector('.alps_animation-bg-grid');
    if (!element || !grid) return;
    gsap.set(grid, { xPercent: 0 });
    if (!reduced)
      observe(
        element,
        gsap
          .timeline({ paused: true, repeat: -1 })
          .to(grid, { xPercent: -15, duration: 20, ease: easing.cssEaseOut })
          .to(grid, { xPercent: 0, duration: 20, ease: easing.cssEaseInOut }),
      );
  });
  const footerTerrain = root.querySelector<HTMLElement>('.content_footer-top');
  const terrainTrack = footerTerrain?.querySelector('.footer_terrain-track');
  if (footerTerrain && terrainTrack && !reduced)
    observe(
      footerTerrain,
      gsap
        .timeline({ paused: true, repeat: -1 })
        // Two mirrored tiles form a seamless period; the next pair is identical.
        .to(terrainTrack, { xPercent: -200, duration: 240, ease: 'none' }),
    );

  ['d9d3840a-d109-46ab-6f59-dfafccf2cb26', 'c1163ffc-27dd-dfdf-6010-edf6b9519426'].forEach((id) => {
    const dot = byMotionId(root, id);
    if (!dot) return;
    gsap.set(dot, { opacity: reduced ? 1 : 0 });
    if (!reduced)
      observe(
        dot,
        gsap
          .timeline({ paused: true, repeat: -1 })
          .to(dot, { opacity: 1, duration: 0.5, ease: 'none' })
          .to(dot, { opacity: 0, duration: 0.3, ease: 'none' }, '+=0.5'),
      );
  });
  const visibility = () =>
    loops.forEach(({ timeline, trigger }) => {
      if (document.hidden || !trigger.isActive) timeline.pause();
      else timeline.play();
    });
  document.addEventListener('visibilitychange', visibility);
  return () => document.removeEventListener('visibilitychange', visibility);
}

function setupNavigation(root: HTMLElement, reduced: boolean) {
  const hero = byMotionId(root, 'e12b7f09-3add-a0b4-700e-c9a343bf9240');
  const navbar = root.querySelector<HTMLElement>('.navbar');
  if (!navbar) return () => {};
  if (!hero) {
    gsap.set(navbar, { y: 0 });
    return () => {};
  }
  let heroVisible = true;
  let disposed = false;
  let animation: gsap.core.Tween | undefined;
  const update = () => {
    if (disposed) return;
    animation?.kill();
    // Keyboard focus must remain visible even before scrolling past the hero.
    const focusWithin = navbar.contains(document.activeElement);
    animation = gsap.to(navbar, {
      y: heroVisible && !focusWithin ? '-4.5rem' : 0,
      duration: reduced || focusWithin ? 0 : 0.5,
      ease: easing.cubicOut,
      overwrite: 'auto',
    });
  };
  gsap.set(navbar, { y: '-4.5rem' });
  const setVisible = (visible: boolean) => {
    heroVisible = visible;
    update();
  };
  ScrollTrigger.create({
    trigger: hero,
    start: 'top bottom',
    end: 'bottom top',
    onLeave: () => setVisible(false),
    onEnterBack: () => setVisible(true),
    onEnter: () => setVisible(true),
    onLeaveBack: () => setVisible(false),
  });
  const focusOut = () => {
    queueMicrotask(update);
  };
  navbar.addEventListener('focusin', update);
  navbar.addEventListener('focusout', focusOut);
  return () => {
    disposed = true;
    animation?.kill();
    navbar.removeEventListener('focusin', update);
    navbar.removeEventListener('focusout', focusOut);
  };
}
